import classNames from "classnames";
import React, { FC, useEffect, useRef, useState } from "react";
import { LuCheck, LuChevronDown } from "react-icons/lu";

import { getInitials } from "../../../constants/constants";
import { IUser } from "../../../types/types";

export const DEFAULT_AVATAR_COLOR = "rgb(30, 58, 138)";

export const AVATAR_PALETTE = [
  "rgb(228, 87, 46)",
  "rgb(192, 57, 43)",
  "rgb(138, 28, 28)",
  "rgb(176, 48, 96)",
  "rgb(244, 63, 94)",
  "rgb(225, 29, 72)",
  "rgb(214, 64, 159)",
  "rgb(194, 41, 138)",
  "rgb(219, 39, 119)",
  "rgb(226, 85, 161)",
  "rgb(134, 15, 134)",
  "rgb(162, 28, 175)",
  "rgb(107, 78, 255)",
  "rgb(105, 56, 239)",
  "rgb(124, 58, 237)",
  "rgb(126, 34, 206)",
  "rgb(91, 33, 182)",
  "rgb(67, 56, 202)",
  "rgb(27, 62, 159)",
  "rgb(29, 78, 216)",
  "rgb(37, 99, 235)",
  "rgb(14, 165, 233)",
  "rgb(2, 132, 199)",
  "rgb(3, 105, 161)",
  "rgb(8, 145, 178)",
  "rgb(14, 116, 144)",
  "rgb(15, 118, 110)",
  "rgb(13, 148, 136)",
  "rgb(14, 89, 67)",
  "rgb(17, 94, 89)",
  "rgb(22, 163, 74)",
  "rgb(21, 128, 61)",
  "rgb(5, 150, 105)",
  "rgb(34, 197, 94)",
  "rgb(101, 163, 13)",
  "rgb(77, 124, 15)",
  "rgb(154, 165, 23)",
  "rgb(132, 155, 28)",
  "rgb(202, 138, 4)",
  "rgb(180, 83, 9)",
  "rgb(234, 88, 12)",
  "rgb(194, 65, 12)",
  "rgb(224, 121, 74)",
  "rgb(146, 64, 14)",
  "rgb(120, 53, 15)",
  "rgb(87, 83, 78)",
  "rgb(68, 64, 60)",
  "rgb(51, 65, 85)",
];

/** Compare colours stored as "rgb(...)" or "#rrggbb". */
export const normalizeColor = (color: string | null | undefined) => {
  const value = (color || "").trim().toLowerCase();
  const hex = value.match(/^#([0-9a-f]{6})$/);
  if (hex) {
    const n = parseInt(hex[1], 16);
    return `rgb(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255})`;
  }
  return value.replace(/\s+/g, "").replace(/,/g, ", ");
};

// Permission ids the presets leave out (see the old EditUserView).
export const SETTINGS_PERMISSION_ID = "3476ece8-0485-4b7f-98b1-378199bf8767";
export const BILLING_PERMISSION_ID = "434d4588-3532-4e3a-ac3c-d294ef2d76a6";
// "Manage subscription & payment" (seat billing, backend Mg-224, owner
// 2026-10-24): the firm's own ProCharted plan and card. NOT "View Billings"
// (the firm's client invoices). Ticked one by one: no preset includes it and
// Admins don't get it automatically.
export const MANAGE_SUBSCRIPTION_PERMISSION_ID =
  "c5b7e2a4-9d31-4f6e-8a2b-5e7f1d3c9b60";

/** Switches outside the access-level presets (kept as they are when the level changes). */
export const INDEPENDENT_PERMISSION_IDS = [MANAGE_SUBSCRIPTION_PERMISSION_ID];
export const isIndependentPermission = (id: string) =>
  INDEPENDENT_PERMISSION_IDS.includes((id || "").toLowerCase());

/** A preset's switches plus the independent switches that were already on. */
export const withIndependentSwitches = (
  preset: string[],
  current: string[],
): string[] => [
  ...preset.filter((id) => !isIndependentPermission(id)),
  ...current.filter(isIndependentPermission),
];

// Fixed order of the Permissions switches (Roadmap 1862); others follow A-Z.
const PERMISSION_ORDER = [
  SETTINGS_PERMISSION_ID,
  "4ef8ede7-80d5-43a4-9ad9-a9e08dbdb7c0", // View FirmsWork
  BILLING_PERMISSION_ID,
  MANAGE_SUBSCRIPTION_PERMISSION_ID,
  "6a6626d5-716e-4dda-8169-e998165b5892", // View Reports (Mg-223)
  "7a3e5c1d-2b4f-4e8a-9c6d-1f0b8e2a4d73", // View Activity Log (Mg-211)
];

// Permissions the Team windows no longer show (owner, 2026-10-21): "View
// Dashboard" does nothing since the Dashboard page was replaced by Reports.
// The database row stays; it is only hidden here.
export const HIDDEN_PERMISSION_IDS = ["b051ebbc-b5a8-46d3-ad6d-bafb82383682"];

const isHiddenPermission = (id: string) =>
  HIDDEN_PERMISSION_IDS.includes((id || "").toLowerCase());

/**
 * The server's permission rows without the hidden ones. "Manage subscription
 * & payment" only shows for firms billed per user (`seatBilling`).
 */
export const visiblePermissions = <T extends { id: string }>(
  permissions: T[] | undefined,
  options?: { seatBilling?: boolean },
): T[] | undefined =>
  permissions?.filter(
    (permission) =>
      !isHiddenPermission(permission.id) &&
      (options?.seatBilling ||
        (permission.id || "").toLowerCase() !==
          MANAGE_SUBSCRIPTION_PERMISSION_ID),
  );

/** A user's permission ids without the hidden ones. */
export const withoutHiddenPermissions = (ids: string[] | null | undefined) =>
  (ids ?? []).filter((id) => !isHiddenPermission(id));

/** The server's permission rows in the order the Edit user window shows them. */
export const sortPermissions = <T extends { id: string; permission: string }>(
  permissions: T[],
): T[] => {
  const rank = (id: string) => {
    const index = PERMISSION_ORDER.indexOf(id.toLowerCase());
    return index === -1 ? PERMISSION_ORDER.length : index;
  };
  return [...permissions].sort(
    (a, b) =>
      rank(a.id) - rank(b.id) ||
      (a.permission || "").localeCompare(b.permission || ""),
  );
};

// Shown when the server has no description for a permission (by name).
const PERMISSION_HELP: Record<string, string> = {
  "view settings": "Access the settings page",
  "view firm's work":
    "See work items across the whole firm, not just their own.",
  "view firmswork":
    "See work items across the whole firm (Work page, Home firm view and reports), not just their own.",
  "view billing": "View the billing and payments page",
  "view billings":
    "View invoices and payments, and the billing reports (unbilled time, unpaid invoices, revenue).",
  "view reports":
    "Opens the Reports section. What each report shows still depends on View FirmsWork (whole firm or own work) and View Billings.",
  "send from any connected email":
    "Lets this user send email sequences from any of the firm's connected inboxes — not just their own.",
  "manage subscription & payment":
    "See your firm's ProCharted plan, seats and bills, and add or change the card. Admins don't get this automatically.",
  "view activity log":
    "Opens the firm-wide Activity Log — a running history of everything that changes across the firm, by every team member.",
};

/** Help text for a permission switch: the server's description, else ours. */
export const permissionHelp = (permission: {
  permission: string;
  description?: string | null;
}) =>
  permission.description ||
  PERMISSION_HELP[(permission.permission || "").trim().toLowerCase()] ||
  "";

export const ACCESS_LEVELS = [
  "Admin",
  "Staff (No Settings)",
  "Staff (No Settings, No Billing)",
  "Custom",
] as const;

/** Permissions a preset access level grants; null for "Custom". */
export const presetPermissions = (
  level: string,
  allPermissionIds: string[],
): string[] | null => {
  // Independent switches are never part of a preset.
  const presetIds = allPermissionIds.filter(
    (id) => !isIndependentPermission(id),
  );
  switch (level) {
    case "Admin":
      return presetIds;
    case "Staff (No Settings)":
      return presetIds.filter((id) => id !== SETTINGS_PERMISSION_ID);
    case "Staff (No Settings, No Billing)":
      return presetIds.filter(
        (id) => id !== SETTINGS_PERMISSION_ID && id !== BILLING_PERMISSION_ID,
      );
    default:
      return null;
  }
};

/** The preset that grants exactly these permissions, otherwise "Custom". */
export const matchAccessLevel = (
  selected: string[],
  allPermissionIds: string[],
) => {
  const chosen = new Set(selected.filter((id) => !isIndependentPermission(id)));
  const match = ACCESS_LEVELS.find((level) => {
    const preset = presetPermissions(level, allPermissionIds);
    return (
      !!preset &&
      preset.length === chosen.size &&
      preset.every((id) => chosen.has(id))
    );
  });
  return match ?? "Custom";
};

export const userFullName = (user: IUser) =>
  [
    user?.userProfile?.firstName,
    user?.userProfile?.middleName,
    user?.userProfile?.lastName,
  ]
    .filter(Boolean)
    .join(" ");

export const UserAvatar: FC<{
  user: IUser;
  color: string;
  size?: "sm" | "md";
}> = ({ user, color, size = "md" }) => (
  <span
    style={{ backgroundColor: color }}
    className={classNames(
      "flex shrink-0 items-center justify-center rounded-full font-bold text-white",
      size === "md" ? "h-11 w-11 text-[15px]" : "h-10 w-10 text-sm",
    )}>
    {getInitials(
      user?.userProfile?.firstName || "",
      user?.userProfile?.lastName || "",
    )}
  </span>
);

export const Toggle: FC<{
  checked: boolean;
  label: string;
  onChange: (checked: boolean) => void;
  /** Shown but can't be changed (e.g. Admins have every permission). */
  disabled?: boolean;
  title?: string;
}> = ({ checked, label, onChange, disabled, title }) => (
  <button
    type={"button"}
    role={"switch"}
    aria-checked={checked}
    aria-label={label}
    aria-disabled={disabled || undefined}
    disabled={disabled}
    title={title}
    onClick={() => !disabled && onChange(!checked)}
    className={classNames(
      "relative h-[22px] w-[38px] shrink-0 rounded-full transition-colors",
      checked ? "bg-[#6B4EFF]" : "bg-[#DCE0E8]",
      disabled && "cursor-not-allowed opacity-50",
    )}>
    <span
      className={classNames(
        "absolute top-[3px] h-4 w-4 rounded-full bg-white shadow-[0_1px_2px_rgba(16,24,40,0.2)] transition-all",
        checked ? "left-[19px]" : "left-[3px]",
      )}
    />
  </button>
);

export const InfoTip: FC<{
  text: string;
  tone?: "purple" | "gray";
  placement?: "top" | "bottom";
}> = ({ text, tone = "gray", placement = "top" }) => (
  <span className={"group relative inline-flex"}>
    <span
      tabIndex={0}
      aria-label={text}
      className={classNames(
        "flex h-[15px] w-[15px] cursor-help items-center justify-center rounded-full text-[10px] font-bold",
        tone === "purple"
          ? "bg-[#EDE9FF] text-[#6B4EFF]"
          : "border border-[#B4B9C6] text-[#9097A8]",
      )}>
      i
    </span>
    <span
      role={"tooltip"}
      className={classNames(
        "pointer-events-none invisible absolute left-1/2 z-20 w-[240px] -translate-x-1/2 rounded-lg bg-[#1F2433] px-3 py-2 text-[11.5px] font-medium leading-snug text-white opacity-0 shadow-lg transition-opacity group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100",
        placement === "top"
          ? "bottom-[calc(100%+6px)]"
          : "top-[calc(100%+6px)]",
      )}>
      {text}
    </span>
  </span>
);

export const ColorPicker: FC<{
  value: string;
  takenColors: string[];
  onChange: (color: string) => void;
  /** Text shown next to the swatch (profile page: "Avatar color"). */
  label?: string;
}> = ({ value, takenColors, onChange, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const taken = new Set(takenColors.map(normalizeColor));
  const current = normalizeColor(value);

  useEffect(() => {
    if (!isOpen) return;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isOpen]);

  return (
    <div ref={ref} className={"relative"}>
      <button
        type={"button"}
        aria-label={"Avatar color"}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className={
          "flex h-8 items-center gap-1.5 rounded-full border border-[#DCE2EA] bg-white pl-1.5 pr-2 hover:border-[#B9AEFF]"
        }>
        <span
          className={"h-5 w-5 rounded-full"}
          style={{ backgroundColor: value }}
        />
        {label && (
          <span
            className={
              "whitespace-nowrap text-[12.5px] font-bold text-[#5F6577]"
            }>
            {label}
          </span>
        )}
        <LuChevronDown size={14} className={"text-[#6B7185]"} />
      </button>
      {isOpen && (
        <div
          className={
            "absolute right-0 top-[calc(100%+6px)] z-20 w-[262px] rounded-xl border border-[#ECEFF3] bg-white p-3.5 shadow-[0_16px_40px_rgba(16,24,40,0.16)]"
          }>
          <div
            className={
              "mb-2.5 text-[11px] font-bold uppercase tracking-wide text-[#8A90A2]"
            }>
            Avatar color
          </div>
          <div className={"grid grid-cols-8 gap-1.5"}>
            {AVATAR_PALETTE.map((color) => {
              const isSelected = normalizeColor(color) === current;
              const isTaken = !isSelected && taken.has(normalizeColor(color));
              return (
                <button
                  key={color}
                  type={"button"}
                  aria-label={`Color ${color}${
                    isTaken ? " (used by a teammate)" : ""
                  }`}
                  aria-pressed={isSelected}
                  onClick={() => {
                    onChange(color);
                    setIsOpen(false);
                  }}
                  style={{ backgroundColor: color }}
                  className={classNames(
                    "relative h-6 w-6 rounded-full transition-transform hover:scale-110",
                    isSelected &&
                      "ring-2 ring-white ring-offset-2 ring-offset-[#6B4EFF]",
                  )}>
                  {isTaken && (
                    <span
                      className={
                        "absolute -right-0.5 -top-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-white text-[#3A4256] shadow"
                      }>
                      <LuCheck size={8} strokeWidth={4} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div
            className={
              "mt-3 flex items-center gap-1.5 border-t border-[#F1F2F5] pt-2.5 text-[11.5px] text-[#8A90A2]"
            }>
            <span
              className={
                "flex h-3 w-3 items-center justify-center rounded-full bg-[#EEF0F3] text-[#3A4256]"
              }>
              <LuCheck size={8} strokeWidth={4} />
            </span>
            Already used by a teammate
          </div>
          <button
            type={"button"}
            onClick={() => {
              onChange(DEFAULT_AVATAR_COLOR);
              setIsOpen(false);
            }}
            className={
              "mt-2 text-[12px] font-semibold text-[#6B4EFF] hover:underline"
            }>
            Reset to default (navy)
          </button>
        </div>
      )}
    </div>
  );
};

/** Admins pass every permission check, so their switches show on (QA batch 16, TEAM-016). */
export const ADMIN_PERMISSIONS_NOTE =
  "Admins have every permission. Pick another access level to choose permissions one by one.";
export const isAdminLevel = (level?: string | null) =>
  (level || "").trim().toLowerCase() === "admin";
